import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import MeetingService from 'services/MeetingService';
import useRequest from 'hooks/useRequest';
import { ActivityLog, Comment, Document, Motion, Reject, TechnicalIssue } from 'store/types/Meeting';
import { upsertById } from 'util/meeting';
import { defaultSnackbarErrorProps } from 'util/layout';
import { useAuthContext } from 'AuthContext';

export interface MeetingStaffContextType {
  logActivity: (activityLog: ActivityLogInput, notification?: string) => void;
  resetSession: () => void;

  setCurrentMotion: (motion?: Motion) => void;
  motions: Motion[];
  firstLoadingMotions?: boolean;
  refetchMotions?: () => void;
  currentMotion?: Motion;
  saveMotion?: () => Promise<Motion | null>;
  deleteMotion?: (id: string) => void;

  setCurrentDocument: (document?: Document) => void;
  documents: Document[];
  refetchDocuments?: () => void;
  currentDocument?: Document;
  saveDocument?: () => Promise<Document | null>;
  deleteDocument?: (id: string) => void;
  documentExists?: boolean;

  setCurrentReject: (reject?: Reject) => void;
  rejects: Reject[];
  refetchRejects?: () => void;
  currentReject?: Reject;
  saveReject?: () => Promise<Reject | null>;
  deleteReject?: (id: string) => void;

  setCurrentTechnicalIssue: (technicalIssue?: TechnicalIssue) => void;
  technicalIssues: TechnicalIssue[];
  refetchTechnicalIssues?: () => void;
  currentTechnicalIssue?: TechnicalIssue;
  saveTechnicalIssue?: () => Promise<TechnicalIssue | null>;
  deleteTechnicalIssue?: (id: string) => void;

  setCurrentComment: (comment?: Comment) => void;
  comments: Comment[];
  refetchComments?: () => void;
  currentComment?: Comment;
  saveComment?: () => Promise<Comment | null>;
  deleteComment?: (id: string) => void;
}

type ActivityLogInput = Omit<ActivityLog, 'id' | 'meetingId'>;

const MeetingStaffContext = createContext<MeetingStaffContextType>({} as MeetingStaffContextType);

interface MeetingStaffProviderProps {
  meetingId: string;
}

export const MeetingStaffProvider: React.FunctionComponent<MeetingStaffProviderProps> = ({ meetingId, children }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { profile } = useAuthContext();
  const [currentMotion, setCurrentMotion] = useState<Motion>();
  const [currentDocument, setCurrentDocument] = useState<Document>();
  const [currentReject, setCurrentReject] = useState<Reject>();
  const [currentTechnicalIssue, setCurrentTechnicalIssue] = useState<TechnicalIssue>();
  const [currentComment, setCurrentComment] = useState<Comment>();

  const staffName = useMemo(
    () => `${profile?.customer.firstname} ${profile?.customer.lastname}`,
    [profile?.customer.firstname, profile?.customer.lastname]
  );

  const getMotionsRequest = useMemo(
    () => (!meetingId ? undefined : () => MeetingService.getMotions(meetingId)),
    [meetingId]
  );
  const {
    data: motions = [],
    firstLoading: firstLoadingMotions,
    refetch: refetchMotions,
  } = useRequest<Motion[]>(getMotionsRequest);

  const getDocumentsRequest = useMemo(
    () => (!meetingId ? undefined : () => MeetingService.getDocuments(meetingId)),
    [meetingId]
  );
  const { data: documents = [], refetch: refetchDocuments } = useRequest<Document[]>(getDocumentsRequest);

  const getRejectsRequest = useMemo(
    () => (!meetingId ? undefined : () => MeetingService.getRejects(meetingId)),
    [meetingId]
  );
  const { data: rejects = [], refetch: refetchRejects } = useRequest<Reject[]>(getRejectsRequest);

  const getTechnicalIssuesRequest = useMemo(
    () => (!meetingId ? undefined : () => MeetingService.getTechnicalIssues(meetingId)),
    [meetingId]
  );
  const { data: technicalIssues = [], refetch: refetchTechnicalIssues } =
    useRequest<TechnicalIssue[]>(getTechnicalIssuesRequest);

  const getCommentsRequest = useMemo(
    () => (!meetingId ? undefined : () => MeetingService.getComments(meetingId)),
    [meetingId]
  );
  const { data: comments = [], refetch: refetchComments } = useRequest<Comment[]>(getCommentsRequest);

  const documentRejects = useMemo(
    () => rejects.filter((r) => r.documentId === currentDocument?.id),
    [currentDocument?.id, rejects]
  );
  const documentTechnicalIssues = useMemo(
    () => technicalIssues?.filter((t) => t.documentId === currentDocument?.id),
    [currentDocument?.id, technicalIssues]
  );
  const documentComments = useMemo(
    () => comments?.filter((c) => c.documentId === currentDocument?.id),
    [comments, currentDocument?.id]
  );

  const documentExists = useMemo(
    () => documents.some((d) => d.id === currentDocument?.id),
    [currentDocument?.id, documents]
  );

  const saveMotion = useCallback(async (): Promise<Motion | null> => {
    if (!currentMotion?.title || !currentMotion?.statement) {
      enqueueSnackbar('Title and Motion are required fields', { variant: 'warning' });
      return null;
    }

    const updatedMotions = upsertById(motions, currentMotion);

    try {
      await MeetingService.saveMotions(meetingId, updatedMotions);
    } catch (error) {
      enqueueSnackbar(`Failed to save motion`, defaultSnackbarErrorProps);
      console.error('Error saving motion:', error);

      return null;
    }

    return currentMotion;
  }, [currentMotion, enqueueSnackbar, meetingId, motions]);

  const deleteMotion = useCallback(
    (id) => {
      const updatedMotions = motions.filter((m) => m.id !== id);

      MeetingService.clearVotes(meetingId, id);
      MeetingService.saveMotions(meetingId, updatedMotions)
        .then(() => {
          enqueueSnackbar(`Motion deleted`, { variant: 'success' });
        })
        .catch((error: string) => {
          enqueueSnackbar(`Failed to delete motion`, defaultSnackbarErrorProps);
          console.error('Error deleting motion:', error);
        });
    },
    [motions, meetingId, enqueueSnackbar]
  );

  const saveDocument = useCallback(async (): Promise<Document | null> => {
    if (!currentDocument?.number || !currentDocument?.title) {
      enqueueSnackbar(`Number and title are required fields`, { variant: 'warning' });
      return null;
    }

    const updatedDocuments = upsertById(documents, currentDocument);

    try {
      await MeetingService.saveDocuments(
        meetingId,
        updatedDocuments.sort((a, b) =>
          (a.number ?? '').localeCompare(b.number ?? '', undefined, {
            numeric: true,
            sensitivity: 'base',
          })
        )
      );
    } catch (error) {
      enqueueSnackbar(`Failed to save document`, defaultSnackbarErrorProps);
      console.error('Error saving document:', error);

      return null;
    }

    return currentDocument;
  }, [currentDocument, documents, enqueueSnackbar, meetingId]);

  const deleteDocument = useCallback(
    (id) => {
      const updatedDocuments = documents.filter((d) => d.id !== id);

      MeetingService.saveDocuments(meetingId, updatedDocuments)
        .then(() => {
          enqueueSnackbar(`Document deleted`, { variant: 'success' });
        })
        .catch((error: string) => {
          enqueueSnackbar(`Failed to delete document`, defaultSnackbarErrorProps);
          console.error('Error deleting document:', error);
        });
    },
    [documents, enqueueSnackbar, meetingId]
  );

  const saveReject = useCallback(async (): Promise<Reject | null> => {
    if (!documentExists) {
      enqueueSnackbar(`Document must be saved or displayed before adding a reject`, { variant: 'warning' });
      return null;
    }

    if (
      !currentReject?.votingInterest ||
      !currentReject?.rejectedBy ||
      !currentReject?.number ||
      !currentReject?.reference ||
      !currentReject?.text
    ) {
      enqueueSnackbar(`Voting interest, name, number, reference section, and text are required are required fields`, {
        variant: 'warning',
      });
      return null;
    }

    const updatedRejects = upsertById(rejects, currentReject);

    try {
      await MeetingService.saveRejects(meetingId, updatedRejects);
    } catch (error) {
      enqueueSnackbar(`Failed to save reject`, defaultSnackbarErrorProps);
      console.error('Error saving reject:', error);

      return null;
    }

    return currentReject;
  }, [documentExists, currentReject, rejects, enqueueSnackbar, meetingId]);

  const deleteReject = useCallback(
    (id) => {
      const updatedRejects = rejects.filter((t) => t.id !== id);

      MeetingService.saveRejects(meetingId, updatedRejects)
        .then(() => {
          enqueueSnackbar(`Reject deleted`, { variant: 'success' });
        })
        .catch((error: string) => {
          enqueueSnackbar(`Failed to delete reject`, defaultSnackbarErrorProps);
          console.error('Error deleting reject:', error);
        });
    },
    [enqueueSnackbar, meetingId, rejects]
  );

  const saveTechnicalIssue = useCallback(async (): Promise<TechnicalIssue | null> => {
    if (!documentExists) {
      enqueueSnackbar(`Document must be saved or displayed before adding a technical issue`, { variant: 'warning' });
      return null;
    }

    if (!currentTechnicalIssue?.origin || !currentTechnicalIssue?.reference || !currentTechnicalIssue?.reason) {
      enqueueSnackbar(`Origin, referenced section, and reason are required fields`, { variant: 'warning' });
      return null;
    }

    const updatedTechnicalIssues = upsertById(technicalIssues, currentTechnicalIssue);

    try {
      await MeetingService.saveTechnicalIssues(meetingId, updatedTechnicalIssues);
    } catch (error) {
      enqueueSnackbar(`Failed to save technical issue`, defaultSnackbarErrorProps);
      console.error('Error saving technical issue:', error);

      return null;
    }

    return currentTechnicalIssue;
  }, [documentExists, currentTechnicalIssue, technicalIssues, enqueueSnackbar, meetingId]);

  const deleteTechnicalIssue = useCallback(
    (id) => {
      const updatedTechnicalIssues = technicalIssues.filter((t) => t.id !== id);

      MeetingService.saveTechnicalIssues(meetingId, updatedTechnicalIssues)
        .then(() => {
          enqueueSnackbar(`Technical issue deleted`, { variant: 'success' });
        })
        .catch((error: string) => {
          enqueueSnackbar(`Failed to delete technical issue`, defaultSnackbarErrorProps);
          console.error('Error deleting technical issue:', error);
        });
    },
    [enqueueSnackbar, meetingId, technicalIssues]
  );

  const saveComment = useCallback(async (): Promise<Comment | null> => {
    if (!documentExists) {
      enqueueSnackbar(`Document must be saved or displayed before adding a comment`, { variant: 'warning' });
      return null;
    }

    if (!currentComment?.type || !currentComment?.commenter || !currentComment?.number) {
      enqueueSnackbar(`Commenter and number are required fields`, { variant: 'warning' });
      return null;
    }

    const updatedComments = upsertById(comments, currentComment);

    try {
      await MeetingService.saveComments(meetingId, updatedComments);
    } catch (error) {
      enqueueSnackbar(`Failed to save comment`, defaultSnackbarErrorProps);
      console.error('Error saving comment:', error);

      return null;
    }

    return currentComment;
  }, [documentExists, currentComment, comments, enqueueSnackbar, meetingId]);

  const deleteComment = useCallback(
    (id) => {
      const updatedComments = comments.filter((t) => t.id !== id);

      MeetingService.saveComments(meetingId, updatedComments)
        .then(() => {
          enqueueSnackbar(`Comment deleted`, { variant: 'success' });
        })
        .catch((error: string) => {
          enqueueSnackbar(`Failed to delete comment`, defaultSnackbarErrorProps);
          console.error('Error deleting comment:', error);
        });
    },
    [comments, enqueueSnackbar, meetingId]
  );

  const logActivity = useCallback(
    (activityLog: ActivityLogInput, notification?: string) => {
      const log: ActivityLog = {
        id: `activity-log-${Date.now()}`,
        meetingId,
        staffName,
        ...activityLog,
      };

      MeetingService.addActivityLog(meetingId, log)
        .then(() => {
          if (notification) enqueueSnackbar(notification, { variant: 'success' });
        })
        .catch((error: string) => {
          console.error('Error logging activity:', error);
        });
    },
    [enqueueSnackbar, meetingId, staffName]
  );

  const resetSession = useCallback(() => {
    setCurrentMotion(undefined);
    setCurrentDocument(undefined);
    setCurrentReject(undefined);
    setCurrentTechnicalIssue(undefined);
    setCurrentComment(undefined);
  }, []);

  const context: MeetingStaffContextType = {
    logActivity,
    resetSession,

    setCurrentMotion,
    motions,
    firstLoadingMotions,
    refetchMotions,
    currentMotion,
    saveMotion,
    deleteMotion,

    setCurrentDocument,
    documents,
    refetchDocuments,
    currentDocument,
    saveDocument,
    deleteDocument,
    documentExists,

    setCurrentReject,
    rejects: documentRejects,
    refetchRejects,
    currentReject,
    saveReject,
    deleteReject,

    setCurrentTechnicalIssue,
    technicalIssues: documentTechnicalIssues,
    refetchTechnicalIssues,
    currentTechnicalIssue,
    saveTechnicalIssue,
    deleteTechnicalIssue,

    setCurrentComment,
    comments: documentComments,
    refetchComments,
    currentComment,
    saveComment,
    deleteComment,
  };

  return <MeetingStaffContext.Provider value={context}>{children}</MeetingStaffContext.Provider>;
};

export default MeetingStaffProvider;

export const useMeetingStaffContext = (): MeetingStaffContextType => {
  const context = useContext(MeetingStaffContext);

  if (context === undefined) {
    throw new Error('MeetingStaffContext was used outside of its Provider');
  }

  return context;
};
